import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button, InputAdornment, alpha,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Title as TitleIcon,
  School as UniversityIcon,
  LocationOn as LocationIcon,
  Notes as RemarksIcon,
  Flag as FlagIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as ReplaceIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageAnalyses } from '../../../utils/analysisPermissions';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import { SUCCESS } from '../../common/SaaSTokens';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';
import {
  ANALYSIS_DOCUMENT_ACCEPT,
  ANALYSIS_DOCUMENT_MAX_BYTES,
  getAnalysisDocumentFilename,
  getAnalysisDocumentKind,
  getAnalysisDocumentTypeLabel,
  isAllowedAnalysisDocument,
} from '../../../utils/analysisDocument';
import { toStorageUrl } from '../../../utils/storageUrl';
import { fetchAnalysisPreviewBlob, openAnalysisDocumentInNewTab, openBlobUrlInNewTab } from '../../../utils/protectedFilePreview';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B         = { 700: '#1D4ED8' };
const NEON      = '#38BDF8';
const NEON2     = '#818CF8';
const NEON3     = '#34D399';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const TEXT_BODY    = '#E2E8F0';
const TEXT_MUTED   = '#94A3B8';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-glow-in    { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes drop-zone-pulse { 0%,100% { border-color: rgba(56,189,248,0.6); } 50% { border-color: rgba(56,189,248,0.9); box-shadow: 0 0 20px 4px rgba(56,189,248,0.15); } }
  @keyframes file-in         { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;

// ─── Shared field sx ───────────────────────────────────────────────────────────
const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18) },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': { color: alpha(TEXT_BODY, 0.85), '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 } },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
};

const StyledField = ({ sx, ...props }) => <TextField {...props} sx={{ ...inputRootSx, ...sx }} />;

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>{children}</CommonSectionCard>
);

// ─── Helpers ───────────────────────────────────────────────────────────────────
const formatBytes = (b) =>
  b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

const TooltipBtn = ({ label, onClick, color, children }) => (
  <Box title={label} onClick={onClick} sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: '7px', cursor: 'pointer',
    color: color ?? alpha(TEXT_MUTED, 0.7), border: `1px solid ${alpha(color ?? NEON, 0.18)}`,
    transition: 'all 0.18s ease',
    '&:hover': { background: alpha(color ?? NEON, 0.1), color: color ?? NEON, borderColor: alpha(color ?? NEON, 0.4) },
  }}>
    {children}
  </Box>
);

// ─── Upload Zone ───────────────────────────────────────────────────────────────
const AnalysisUploadZone = ({
  formData, filePreviewUrl, isEditMode, analysis,
  documentPdfError, fileInputRef, onFileChange, onFileDrop, onRemoveFile,
  onPreviewError,
}) => {
  const [isDragOver,  setIsDragOver]  = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hasNewFile  = Boolean(formData.document_pdf);
  const hasExisting = isEditMode && analysis?.document_pdf && !hasNewFile;
  const hasAnyFile  = hasNewFile || hasExisting;
  const previewKind = hasNewFile
    ? getAnalysisDocumentKind(formData.document_pdf)
    : getAnalysisDocumentKind(analysis?.document_pdf);
  const isPdfPreview = previewKind === 'pdf';
  const FileTypeIcon = previewKind === 'excel' ? ExcelIcon : PdfIcon;
  const fileTypeColor = previewKind === 'excel' ? NEON3 : '#F87171';

  const dragHandlers = {
    onDragOver:  (e) => { e.preventDefault(); setIsDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); setIsDragOver(false); },
    onDrop:      (e) => { setIsDragOver(false); onFileDrop(e); },
  };

  const handleOpenInTab = async () => {
    try {
      if (hasNewFile && filePreviewUrl) {
        openBlobUrlInNewTab(filePreviewUrl);
        onPreviewError?.(null);
        return;
      }
      if (hasExisting && filePreviewUrl?.startsWith('blob:')) {
        openBlobUrlInNewTab(filePreviewUrl);
        onPreviewError?.(null);
        return;
      }
      if (hasExisting && analysis?.id) {
        await openAnalysisDocumentInNewTab(analysis.id);
        onPreviewError?.(null);
      }
    } catch (err) {
      onPreviewError?.(err.message || 'Failed to open document.');
    }
  };

  const PreviewPanel = ({ borderColor }) => previewOpen && filePreviewUrl ? (
    <Box sx={{ borderTop: `1px solid ${alpha(borderColor, 0.15)}`, background: alpha(SURFACE_MID, 0.5) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: `1px solid ${alpha(NEON, 0.08)}` }}>
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6), letterSpacing: 0.5 }}>
          {hasNewFile ? 'PREVIEW' : 'CURRENT FILE PREVIEW'}
        </Typography>
        <Button size="small" onClick={handleOpenInTab}
          startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
          sx={{ px: 1.25, py: 0.3, borderRadius: '6px', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.72rem', border: `1px solid ${alpha(NEON, 0.3)}`, color: NEON, '&:hover': { background: alpha(NEON, 0.08) } }}
        >
          Open in tab
        </Button>
      </Box>
      {isPdfPreview ? (
        <Box sx={{ height: { xs: 300, md: 420 } }}>
          <iframe src={`${filePreviewUrl}#toolbar=0&navpanes=0&view=FitH`} style={{ width: '100%', height: '100%', border: 'none' }} title="PDF Preview" />
        </Box>
      ) : (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 1.5, py: 5, px: 3, textAlign: 'center',
          minHeight: { xs: 220, md: 280 },
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: '12px',
            background: `linear-gradient(135deg, ${alpha(NEON3, 0.2)}, ${alpha(NEON3, 0.06)})`,
            border: `1px solid ${alpha(NEON3, 0.35)}`,
          }}>
            <ExcelIcon sx={{ fontSize: 26, color: NEON3 }} />
          </Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: TEXT_BODY }}>
            Excel preview not available inline
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_MUTED, 0.75), maxWidth: 360 }}>
            Open the file in a new tab or download it to view in Excel or another spreadsheet app.
          </Typography>
        </Box>
      )}
    </Box>
  ) : null;

  // ── No file ────────────────────────────────────────────────────────────────
  if (!hasAnyFile) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box onClick={() => fileInputRef.current?.click()} {...dragHandlers} sx={{
        position: 'relative', overflow: 'hidden', p: { xs: 3, md: 4 }, textAlign: 'center',
        borderRadius: '12px', cursor: 'pointer',
        border: `2px dashed ${isDragOver ? NEON : alpha(NEON, 0.3)}`,
        background: isDragOver
          ? `radial-gradient(ellipse at center, ${alpha(NEON, 0.07)} 0%, ${alpha(SURFACE_UP, 0.6)} 70%)`
          : alpha(SURFACE_UP, 0.35),
        transition: 'all 0.25s ease',
        animation: isDragOver ? 'drop-zone-pulse 1s ease infinite' : 'none',
        '&:hover': { borderColor: alpha(NEON, 0.55), boxShadow: `0 0 24px ${alpha(NEON, 0.1)}` },
      }}>
        {[
          { top: 8, left: 8,    bt: true,  bb: false, bl: true,  br: false },
          { top: 8, right: 8,   bt: true,  bb: false, bl: false, br: true  },
          { bottom: 8, left: 8,  bt: false, bb: true,  bl: true,  br: false },
          { bottom: 8, right: 8, bt: false, bb: true,  bl: false, br: true  },
        ].map((c, i) => (
          <Box key={i} sx={{
            position: 'absolute', top: c.top ?? 'auto', bottom: c.bottom ?? 'auto',
            left: c.left ?? 'auto', right: c.right ?? 'auto', width: 12, height: 12,
            borderTop:    c.bt ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
            borderBottom: c.bb ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
            borderLeft:   c.bl ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
            borderRight:  c.br ? `2px solid ${alpha(NEON, isDragOver ? 0.9 : 0.4)}` : 'none',
          }} />
        ))}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 52, height: 52, borderRadius: '14px', mb: 2,
          background: `linear-gradient(135deg, ${alpha(NEON, isDragOver ? 0.2 : 0.1)}, ${alpha(B[700], isDragOver ? 0.3 : 0.15)})`,
          border: `1px solid ${alpha(NEON, isDragOver ? 0.5 : 0.2)}`,
          transition: 'all 0.25s ease',
        }}>
          <UploadIcon sx={{ fontSize: 24, color: isDragOver ? NEON : alpha(NEON, 0.7) }} />
        </Box>
        <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1rem', color: isDragOver ? NEON : alpha(TEXT_BODY, 0.9), mb: 0.5 }}>
          {isDragOver ? 'Drop your file here' : 'Drop PDF or Excel here, or click to browse'}
          {!isEditMode && <Box component="span" sx={{ color: '#EF4444', ml: 0.5 }}>*</Box>}
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: alpha(TEXT_MUTED, 0.7) }}>
          PDF, XLSX, or XLS · max 15 MB
        </Typography>
      </Box>
      {documentPdfError && (
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: '#F87171', pl: 0.5 }}>
          ⚠ {documentPdfError}
        </Typography>
      )}
      <input ref={fileInputRef} type="file" accept={ANALYSIS_DOCUMENT_ACCEPT} onChange={onFileChange} style={{ display: 'none' }} />
    </Box>
  );

  // ── New file selected ──────────────────────────────────────────────────────
  if (hasNewFile) {
    const file = formData.document_pdf;
    const fileKind = getAnalysisDocumentKind(file);
    const fileLabel = getAnalysisDocumentTypeLabel(fileKind);
    const NewFileIcon = fileKind === 'excel' ? ExcelIcon : PdfIcon;
    const newFileColor = fileKind === 'excel' ? NEON3 : '#F87171';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, animation: 'file-in 0.3s ease' }}>
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: `1px solid ${alpha(NEON3, 0.3)}`, background: `linear-gradient(135deg, ${alpha(NEON3, 0.05)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '8px', background: `linear-gradient(135deg, ${alpha(newFileColor, 0.2)}, ${alpha(newFileColor, 0.06)})`, border: `1px solid ${alpha(newFileColor, 0.3)}` }}>
              <NewFileIcon sx={{ fontSize: 20, color: newFileColor }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
                <CheckCircleIcon sx={{ fontSize: 13, color: NEON3, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: TEXT_BODY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.63rem', color: alpha(TEXT_MUTED, 0.8) }}>{formatBytes(file.size)}</Typography>
                {[{ label: fileLabel, color: newFileColor }, { label: 'READY', color: NEON3 }].map(({ label, color }) => (
                  <Box key={label} sx={{ px: 0.75, py: 0.15, borderRadius: '4px', background: alpha(color, 0.1), border: `1px solid ${alpha(color, 0.25)}` }}>
                    <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.58rem', color, fontWeight: 600, letterSpacing: 0.5 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              <TooltipBtn label="Preview" onClick={() => setPreviewOpen(v => !v)}>
                {previewOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              </TooltipBtn>
              <TooltipBtn label="Replace" onClick={() => fileInputRef.current?.click()} color={NEON}>
                <ReplaceIcon sx={{ fontSize: 15 }} />
              </TooltipBtn>
              <TooltipBtn label="Remove" onClick={onRemoveFile} color="#F87171">
                <DeleteIcon sx={{ fontSize: 15 }} />
              </TooltipBtn>
            </Box>
          </Box>
          <PreviewPanel borderColor={NEON3} />
        </Box>
        {!previewOpen && (
          <Box onClick={() => setPreviewOpen(true)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer', border: `1px dashed ${alpha(NEON, 0.15)}`, background: alpha(SURFACE_UP, 0.2), transition: 'all 0.2s ease', '&:hover': { background: alpha(NEON, 0.04), borderColor: alpha(NEON, 0.3) } }}>
            <ExpandMoreIcon sx={{ fontSize: 14, color: alpha(TEXT_MUTED, 0.5) }} />
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.73rem', color: alpha(TEXT_MUTED, 0.5) }}>Show preview</Typography>
          </Box>
        )}
        <input ref={fileInputRef} type="file" accept={ANALYSIS_DOCUMENT_ACCEPT} onChange={onFileChange} style={{ display: 'none' }} />
      </Box>
    );
  }

  // ── Edit mode: existing file ───────────────────────────────────────────────
  const existingFilename = getAnalysisDocumentFilename(analysis?.name, analysis?.document_pdf);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: `1px solid ${alpha(NEON2, 0.25)}`, background: `linear-gradient(135deg, ${alpha(NEON2, 0.04)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '8px', background: `linear-gradient(135deg, ${alpha(fileTypeColor, 0.18)}, ${alpha(fileTypeColor, 0.05)})`, border: `1px solid ${alpha(fileTypeColor, 0.25)}` }}>
            <FileTypeIcon sx={{ fontSize: 20, color: fileTypeColor }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: TEXT_BODY, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {existingFilename}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', background: alpha(NEON2, 0.1), border: `1px solid ${alpha(NEON2, 0.25)}` }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.58rem', color: NEON2, fontWeight: 600, letterSpacing: 0.5 }}>CURRENT</Typography>
              </Box>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.63rem', color: alpha(TEXT_MUTED, 0.6) }}>Saved on server</Typography>
            </Box>
          </Box>
          <TooltipBtn label="Preview" onClick={() => setPreviewOpen(v => !v)}>
            {previewOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
          </TooltipBtn>
        </Box>
        <PreviewPanel borderColor={NEON2} />
      </Box>
      <Box onClick={() => fileInputRef.current?.click()} {...dragHandlers} sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
        p: 1.5, borderRadius: '8px', cursor: 'pointer',
        border: `1.5px dashed ${isDragOver ? alpha(NEON, 0.7) : alpha(NEON, 0.2)}`,
        background: isDragOver ? alpha(NEON, 0.06) : alpha(SURFACE_UP, 0.25),
        transition: 'all 0.2s ease', '&:hover': { background: alpha(NEON, 0.04), borderColor: alpha(NEON, 0.4) },
      }}>
        <ReplaceIcon sx={{ fontSize: 16, color: alpha(NEON, isDragOver ? 0.9 : 0.5) }} />
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.65) }}>
          Replace with a new PDF or Excel file
        </Typography>
      </Box>
      <input ref={fileInputRef} type="file" accept={ANALYSIS_DOCUMENT_ACCEPT} onChange={onFileChange} style={{ display: 'none' }} />
    </Box>
  );
};

const ANALYSIS_VALIDATION_FIELDS = new Set([
  'name', 'type', 'university', 'country', 'document_pdf', 'remark', 'publicity',
]);
const typeOptions = [
  { value: 'training',      label: 'Training'      },
  { value: 'domestic',      label: 'Domestic'      },
  { value: 'international', label: 'International' },
];

const publicityOptions = [
  { value: 'public',  label: 'Public — Visible to all team members' },
  { value: 'private', label: 'Private — Accessible to Team Leader and Game Management Member only' },
];
const countryOptions = [
  'Malaysia','Indonesia','Thailand','Vietnam','Cambodia','Laos',
  'China','Japan','India','Sri Lanka','Nepal','Mongolia',
  'Hong Kong','Egypt','Fiji',
].map(c => ({ value: c, label: c }));

// ─── Main Component ────────────────────────────────────────────────────────────
const AnalysisForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();
  const { user }  = useAuth();

  const fileUploadedRef = useRef(false);
  const fileInputRef    = useRef(null);

  const [loading,                 setLoading]                 = useState(false);
  const [dataLoading,             setDataLoading]             = useState(Boolean(id));
  const [error,                   setError]                   = useState(null);
  const [success,                 setSuccess]                 = useState(false);
  const [analysis,                setAnalysis]                = useState(location.state?.analysis);
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [filePreviewUrl,          setFilePreviewUrl]          = useState(null);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest,    setPendingUpdateRequest]    = useState(null);

  const [formData, setFormData] = useState({
    name: '', type: '', university: '', country: '', document_pdf: null, remark: '', publicity: '',
  });

  const isEditMode = Boolean(analysis || id);
  const canManage  = canManageAnalyses(user);

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'analysis',
    userId: user?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  useEffect(() => {
    const cssId = 'analysisform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style'); el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (!canManage) { navigate('/analyses'); return; }
    const fetchData = async () => {
      try {
        setDataLoading(true);
        if (id && !analysis) {
          const r = await axiosInstance.get(`/analyses/${id}`);
          const d = r.data;
          setAnalysis(d);
          setFormData({ name: d.name || '', type: d.type || '', university: d.university || '', country: d.country || '', document_pdf: null, remark: d.remark || '', publicity: d.publicity || 'public' });
        }
        setError(null);
      } catch { setError('Failed to fetch data'); }
      finally { setDataLoading(false); }
    };
    fetchData();
  }, [id, analysis, canManage, navigate]);

  useEffect(() => {
    if (fileUploadedRef.current) return;
    if (analysis) {
      setFormData({ name: analysis.name || '', type: analysis.type || '', university: analysis.university || '', country: analysis.country || '', document_pdf: null, remark: analysis.remark || '', publicity: analysis.publicity || 'public' });
    } else if (!id) {
      setFormData({ name: '', type: '', university: '', country: '', document_pdf: null, remark: '', publicity: '' });
    }
  }, [analysis, id]);

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;

    if (formData.document_pdf) {
      const url = URL.createObjectURL(formData.document_pdf);
      revokeUrl = url;
      setFilePreviewUrl(url);
    } else if (isEditMode && analysis?.document_pdf) {
      const publicUrl = toStorageUrl(analysis.document_pdf);
      setFilePreviewUrl(publicUrl || null);

      if (getAnalysisDocumentKind(analysis.document_pdf) === 'pdf' && (analysis?.id || id)) {
        fetchAnalysisPreviewBlob(analysis?.id || id)
          .then((preview) => {
            if (cancelled) {
              URL.revokeObjectURL(preview.url);
              return;
            }
            if (revokeUrl) URL.revokeObjectURL(revokeUrl);
            revokeUrl = preview.url;
            setFilePreviewUrl(preview.url);
          })
          .catch(() => {
            if (!cancelled) setFilePreviewUrl(publicUrl || null);
          });
      }
    } else {
      setFilePreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [formData.document_pdf, analysis, isEditMode, id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };
  const handleNativeChange = e => handleChange(e.target.name, e.target.value);

  const processSelectedFile = (file) => {
    if (!file) return;
    if (!isAllowedAnalysisDocument(file)) { setError('Please select a PDF, XLSX, or XLS file'); return; }
    if (file.size > ANALYSIS_DOCUMENT_MAX_BYTES) { setError('Document must be less than 15 MB'); return; }
    fileUploadedRef.current = true;
    setFormData(prev => ({ ...prev, document_pdf: file }));
    setError(null);
    if (showFieldErrors && validationErrors.document_pdf) {
      setValidationErrors(prev => { const n = { ...prev }; delete n.document_pdf; return n; });
    }
  };
  const handleFileChange = (e) => processSelectedFile(e.target.files[0]);
  const handleFileDrop   = (e) => { e.preventDefault(); e.stopPropagation(); processSelectedFile(e.dataTransfer?.files?.[0]); };
  const handleRemoveFile = ()  => { fileUploadedRef.current = false; setFormData(prev => ({ ...prev, document_pdf: null })); setFilePreviewUrl(null); };

  const collectValidationErrors = () => {
    const errors = {};
    if (!formData.name?.trim())       errors.name       = 'Name is required';
    if (!formData.type)               errors.type       = 'Type is required';
    if (!formData.university?.trim()) errors.university = 'University is required';
    if (!formData.country)            errors.country    = 'Country is required';
    if (!formData.publicity)          errors.publicity  = 'Visibility is required';
    if (!isEditMode && !formData.document_pdf) errors.document_pdf = 'Analysis document is required (PDF or Excel)';
    return errors;
  };

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, ANALYSIS_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!ANALYSIS_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const canSubmit = Boolean(
    formData.name.trim() &&
    formData.type &&
    formData.university.trim() &&
    formData.country &&
    formData.publicity &&
    (isEditMode || formData.document_pdf),
  );
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  const buildFormData = () => {
    const fd = new FormData();
    Object.keys(formData).forEach(k => {
      if (k === 'document_pdf') { if (formData.document_pdf) fd.append(k, formData.document_pdf); }
      else fd.append(k, formData[k]);
    });
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(null);
    if (isEditMode) {
      setValidationErrors({});
      setShowFieldErrors(false);
      const fd = buildFormData(); fd.append('_method', 'PUT');
      setPendingUpdateRequest({ url: `/analyses/${analysis?.id || id}`, data: fd });
      setConfirmUpdateDialogOpen(true);
      return;
    }
    if (!validateForm()) return;
    try {
      setLoading(true);
      await axiosInstance.post('/analyses', buildFormData(), { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!isEditMode) formDraft.clearDraft();
      setSuccess(true); setTimeout(() => navigate('/analyses'), 2000);
    } catch (err) {
      handleApiError(err, 'Failed to save analysis');
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors, setValidationErrors, setError,
        setConfirmUpdateDialogOpen, setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post(pendingUpdateRequest.url, pendingUpdateRequest.data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true); setTimeout(() => navigate('/analyses'), 2000);
    } catch (err) { handleApiError(err, 'Failed to update analysis'); }
    finally { setLoading(false); setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }
  };

  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading analysis form" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isEditMode ? 'Edit Analysis' : 'Create New Analysis'}
        subtitle={isEditMode ? 'Update your research analysis information' : 'Add a new research analysis to your library'}
        icon={AnalyticsIcon} showBackButton onBackClick={() => navigate(-1)} backButtonText="Back to Analyses"
      />

      <FeedbackMessage
        message={success ? `Analysis ${isEditMode ? 'updated' : 'created'} successfully!` : ''}
        severity="success" onClose={() => setSuccess(false)}
        sx={{ fontFamily: "'DM Sans', sans-serif", background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />
      <FeedbackMessage
        message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Section 1: All core fields ─────────────────────────────────── */}
          <SectionCard title="Analysis Details" icon={<AnalyticsIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Row 1: Name + Type */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <StyledField
                  fullWidth required label="Analysis Name" name="name" value={formData.name}
                  onChange={handleNativeChange} error={fieldHasError('name')} helperText={fieldHelper('name')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><TitleIcon /></InputAdornment> }}
                />
                <SearchableSelect
                  required label="Type" value={formData.type}
                  onChange={(v) => handleChange('type', v)}
                  options={typeOptions} error={fieldHasError('type')} helperText={fieldHelper('type')}
                  startIcon={<FlagIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                />
              </Box>

              {/* Row 2: University + Country */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <StyledField
                  fullWidth required label="University" name="university" value={formData.university}
                  onChange={handleNativeChange} error={fieldHasError('university')} helperText={fieldHelper('university')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><UniversityIcon /></InputAdornment> }}
                />
                <SearchableSelect
                  required label="Country" value={formData.country}
                  onChange={(v) => handleChange('country', v)}
                  options={countryOptions} error={fieldHasError('country')} helperText={fieldHelper('country')}
                  startIcon={<LocationIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                />
              </Box>

              {/* Row 3: Publicity */}
              <SearchableSelect
                required label="Visibility" value={formData.publicity}
                onChange={(v) => handleChange('publicity', v)}
                options={publicityOptions} error={fieldHasError('publicity')} helperText={fieldHelper('publicity')}
                startIcon={<LockIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
              />
                
              {/* Remarks */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <StyledField
                  fullWidth multiline rows={5} label="Remarks" name="remark" value={formData.remark}
                  onChange={handleNativeChange}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><RemarksIcon /></InputAdornment> }}
                />
              </Box>
            </Box>
          </SectionCard>

          {/* ── Section 2: Document + Remarks ──────────────────────────────── */}
          <SectionCard title="Document" icon={<PdfIcon />} delay={0.08}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2.5 }}>

              {/* Upload zone */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <AnalysisUploadZone
                  formData={formData} filePreviewUrl={filePreviewUrl}
                  isEditMode={isEditMode} analysis={analysis}
                  documentPdfError={fieldHelper('document_pdf')} fileInputRef={fileInputRef}
                  onFileChange={handleFileChange} onFileDrop={handleFileDrop} onRemoveFile={handleRemoveFile}
                  onPreviewError={setError}
                />
              </Box>

            </Box>
          </SectionCard>

          <SubmitActionsCard
            statusText={isEditMode ? 'Save changes to update this analysis.' : 'Complete the form to add a new analysis.'}
            missingRequiredFields={isEditMode ? null : [!formData.name.trim() && 'Name', !formData.type && 'Type', !formData.university.trim() && 'University', !formData.country && 'Country', !formData.publicity && 'Publicity', !formData.document_pdf && 'Analysis PDF'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create analysis."
            cancelLabel="Cancel" onCancel={() => navigate(-1)} cancelDisabled={loading}
            submitLabel={isEditMode ? 'Update Analysis' : 'Create Analysis'}
            submitDisabled={loading || !canSubmit} loading={loading}
            submitStartIcon={<SaveIcon />}
            {...submitDraftProps}
          />

        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        variant="edit" itemType="analysis" itemName={formData.name}
        onConfirm={confirmUpdate} title="Confirm Update"
        description="This action will update the analysis details and cannot be undone."
        confirmLabel={isEditMode ? 'Update Analysis' : 'Create Analysis'}
        confirmDisabled={!canSubmit}
        loading={loading}
      />
    </Container>
  );
};

export default AnalysisForm;
